import type {
  ChannelCostEstimateId,
  PlanningPageQuery,
  PortCostDefaultId,
  PortCostDefaultUpdatedSubscription,
  PortCostEstimatesQuery,
  ShipId,
  VesselAttributeId,
} from '../../graphql';
import {
  usePortCostsByPortAndPrimaryAttributesQuery,
  usePortCostDefaultUpdatedSubscription,
  useShipUpdatedSubscription,
  PortCostEstimatesDocument,
} from '../../graphql';
import { MultiKey } from '../core/MultiKey';
import {
  convertMonetaryValue,
  type CurrencyRates,
  useCurrencyRates,
  useCurrentUser,
} from '../../state/zustand';
import { parseCurrency } from '@seaber/common/lib/currency';
import { useMemo } from 'react';
import { useAllLocations } from '../../lib/allLocations';
import { compact } from 'lodash';
import type { OnDataOptions } from '@apollo/client';
import type {
  ChannelCostIndex,
  ChannelCostRule,
} from '../pnl/channelCost/channelCostIndex';
import {
  buildChannelCostIndex,
  normalizeChannelCostRules,
} from '../pnl/channelCost/channelCostIndex';
import type { LocationId } from '../core/Core';

export const CONFIDENT_VESSEL_NAME_PORT_RECORDS_COUNT = 3;

export const PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY =
  'pc-miss-primary-attribute';
export const PORT_COST_MISSING_PORT_TYPE_KEY = 'pc-miss-port-type';

export type PortCostEstimateItem = {
  id: PortCostDefaultId;
  cost: number;
  currency: string;
};
export type ChannelCostEstimateItem = {
  id: ChannelCostEstimateId;
  durationOverhead: number | null;
  cost: number;
  currency: string;
};

export type ExternalPortCostEstimates = MultiKey<
  {
    min: number | undefined;
    avg: number | undefined;
    median: number | undefined;
    max: number | undefined;
    count: number;
    currency: string;
  },
  [string, string, string]
>;

export type LatestExternalPortCostEstimates = MultiKey<
  {
    portCost: number | undefined;
    currency: string;
  },
  [string, string, string]
>;

export type PortCostEstimateMaps = {
  byName: ExternalPortCostEstimates;
  byPrimaryAttributeId: ExternalPortCostEstimates;
  latestByPrimaryAttributeId: LatestExternalPortCostEstimates;
  defaultById: Map<PortCostDefaultId, number>;
  defaultByVesselId: MultiKey<PortCostEstimateItem, [LocationId, ShipId]>;
  defaultByTypeId: MultiKey<
    PortCostEstimateItem & { typeName: string },
    [LocationId, VesselAttributeId]
  >;
  channelById: Map<ChannelCostEstimateId, ChannelCostRule>;
  channelCostIndex: ChannelCostIndex;
  locationIdsByName: Map<string, LocationId>;
};

/**
 * This function returns the default port cost for a given location and primary vessel attribute ID.
 * 1: Preferred lookup is for the specific location and primary vessel attribute ID.
 * 2: If not found, it looks for the default location (locationName = '*' , considered a wildcard)
 * and the primary vessel attribute ID.
 */
const WILDCARD_LOCATION = '*';

export function getDefaultPortCostByType(
  defaultByTypeId: MultiKey<
    PortCostEstimateItem & { typeName: string },
    [LocationId, VesselAttributeId]
  >,
  locationId: LocationId,
  primaryVesselAttributeId: VesselAttributeId,
  locationIdsByName: Map<string, LocationId>,
) {
  const defaultCost = defaultByTypeId.get(locationId, primaryVesselAttributeId);
  if (defaultCost) {
    return {
      id: defaultCost.id,
      cost: defaultCost.cost,
      currency: defaultCost.currency,
      typeName: defaultCost.typeName,
    };
  } else {
    const defaultLocation = locationIdsByName.get(WILDCARD_LOCATION);
    const defaultCostForAllLocations = defaultLocation
      ? defaultByTypeId.get(defaultLocation, primaryVesselAttributeId)
      : undefined;
    if (defaultCostForAllLocations) {
      return {
        id: defaultCostForAllLocations.id,
        cost: defaultCostForAllLocations.cost,
        currency: defaultCostForAllLocations.currency,
        typeName: defaultCostForAllLocations.typeName,
      };
    }
  }
  return null;
}

export function portCostEstimatesToMaps(
  portCostEstimates: PortCostEstimatesQuery | undefined,
  locationIdsByName: Map<string, LocationId>,
  currencyRates: CurrencyRates | null,
): PortCostEstimateMaps {
  const byName: ExternalPortCostEstimates = new MultiKey(3);
  const byPrimaryAttributeId: ExternalPortCostEstimates = new MultiKey(3);
  const latestByPrimaryAttributeId: LatestExternalPortCostEstimates =
    new MultiKey(3);
  const defaultById: Map<PortCostDefaultId, number> = new Map();
  const defaultByVesselId: MultiKey<
    PortCostEstimateItem,
    [LocationId, ShipId]
  > = new MultiKey(2);
  const defaultByTypeId: MultiKey<
    PortCostEstimateItem & { typeName: string },
    [LocationId, VesselAttributeId]
  > = new MultiKey(2);
  const channelById: Map<ChannelCostEstimateId, ChannelCostRule> = new Map();

  for (const estimate of portCostEstimates?.allPortCostDefaults?.nodes ?? []) {
    if (!estimate) continue;
    const locationId = estimate?.locationId;
    const vesselId = estimate?.vesselId;
    const typeId = estimate?.vesselTypeId;
    const typeName = estimate.vesselAttributeByVesselTypeId?.name;
    const id = estimate.id;
    const currency = estimate.currency;
    const cost = convertMonetaryValue(+estimate?.cost, currency, currencyRates);
    if (!locationId || !cost) continue;

    defaultById.set(id, cost);

    if (vesselId) {
      defaultByVesselId.set({ id, cost, currency }, locationId, vesselId);
    }
    if (typeId && typeName) {
      defaultByTypeId.set(
        {
          id,
          cost,
          currency,
          typeName,
        },
        locationId,
        typeId,
      );
    }
  }
  for (const estimate of portCostEstimates?.allPortCostsByPortAndVesselNames
    ?.nodes ?? []) {
    if (!estimate || !estimate.portName || !estimate.vesselName) continue;
    const { portName, vesselName, max, avg, median, min, count, portType } =
      estimate;
    if (count && count >= CONFIDENT_VESSEL_NAME_PORT_RECORDS_COUNT) {
      const portCostCurrency = parseCurrency(
        estimate.portCostCurrency ?? 'EUR',
      );
      byName.set(
        {
          min: min
            ? (convertMonetaryValue(+min, portCostCurrency, currencyRates) ??
              undefined)
            : undefined,
          avg: avg
            ? (convertMonetaryValue(+avg, portCostCurrency, currencyRates) ??
              undefined)
            : undefined,
          median: median
            ? (convertMonetaryValue(+median, portCostCurrency, currencyRates) ??
              undefined)
            : undefined,
          max: max
            ? (convertMonetaryValue(+max, portCostCurrency, currencyRates) ??
              undefined)
            : undefined,
          currency: portCostCurrency,
          count: count ? +count : 0,
        },
        portName.toUpperCase(),
        vesselName,
        portType?.length ? portType : PORT_COST_MISSING_PORT_TYPE_KEY,
      );
    }
  }
  for (const estimate of portCostEstimates
    ?.allPortCostsByPortAndPrimaryAttributes?.nodes ?? []) {
    if (!estimate || !estimate.portName) continue;
    const {
      portName,
      primaryAttributeId,
      max,
      avg,
      median,
      min,
      count,
      portType,
    } = estimate;
    const portCostCurrency = parseCurrency(estimate.portCostCurrency ?? 'EUR');
    byPrimaryAttributeId.set(
      {
        min: min
          ? (convertMonetaryValue(+min, portCostCurrency, currencyRates) ??
            undefined)
          : undefined,
        avg: avg
          ? (convertMonetaryValue(+avg, portCostCurrency, currencyRates) ??
            undefined)
          : undefined,
        median: median
          ? (convertMonetaryValue(+median, portCostCurrency, currencyRates) ??
            undefined)
          : undefined,
        max: max
          ? (convertMonetaryValue(+max, portCostCurrency, currencyRates) ??
            undefined)
          : undefined,
        currency: portCostCurrency,
        count: count ? +count : 0,
      },
      portName.toUpperCase(),
      primaryAttributeId
        ? primaryAttributeId.toString()
        : PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
      portType?.length ? portType : PORT_COST_MISSING_PORT_TYPE_KEY,
    );
  }
  for (const node of portCostEstimates
    ?.allLatestPortCostByPortAndPrimaryAttributes?.nodes ?? []) {
    if (!node || !node.portName || !node.portCost) {
      continue;
    }

    const portCostCurrency = parseCurrency(node.portCostCurrency ?? 'EUR');
    latestByPrimaryAttributeId.set(
      {
        portCost:
          convertMonetaryValue(
            +node.portCost,
            portCostCurrency,
            currencyRates,
          ) ?? undefined,
        currency: portCostCurrency,
      },
      node.portName.toUpperCase(),
      node.primaryAttributeId?.toString() ??
        PORT_COST_MISSING_PRIMARY_ATTRIBUTE_KEY,
      node.portType?.length ? node.portType : PORT_COST_MISSING_PORT_TYPE_KEY,
    );
  }

  const normalizedChannelCosts = normalizeChannelCostRules(
    portCostEstimates?.allChannelCostEstimates?.nodes ?? [],
    currencyRates,
  );

  for (const estimate of normalizedChannelCosts) {
    if (!estimate || !estimate.channelId) continue;
    channelById.set(estimate.id, estimate);
  }

  const channelCostIndex = buildChannelCostIndex(normalizedChannelCosts);

  return {
    byName,
    byPrimaryAttributeId,
    latestByPrimaryAttributeId,
    defaultById,
    defaultByVesselId,
    defaultByTypeId,
    channelById,
    channelCostIndex,
    locationIdsByName,
  };
}

export function usePortCostEstimates(
  data: PlanningPageQuery | undefined,
): PortCostEstimateMaps {
  const currentUser = useCurrentUser();
  const organizationId = currentUser?.organizationId ?? null;
  const skip = typeof window === 'undefined' || !organizationId;
  const org = (organizationId ?? -1).toString();

  const locations = useAllLocations();
  const locationIdsByNames = useMemo(
    () =>
      new Map(
        compact(locations.data?.allLocations?.nodes).map((location) => [
          location.name.toUpperCase(),
          location.id,
        ]) ?? [],
      ),
    [locations],
  );

  usePortCostDefaultUpdatedSubscription({
    variables: {
      org,
    },
    skip,
    onData: async function ({
      data,
      client,
    }: OnDataOptions<PortCostDefaultUpdatedSubscription>) {
      const update = data.data?.portCostDefaultUpdated;
      if (update) {
        const { portCostDefault, event } = update;
        const dataInStore = client.readQuery<PortCostEstimatesQuery>({
          query: PortCostEstimatesDocument,
        });
        if (!dataInStore) return;
        const allPortCostDefaults = dataInStore?.allPortCostDefaults;
        const nodes = allPortCostDefaults?.nodes;
        if (!nodes) {
          return;
        }

        if (portCostDefault) {
          const id = portCostDefault.id;
          if (!nodes.find((o) => o?.id === id)) {
            client.writeQuery({
              query: PortCostEstimatesDocument,
              data: {
                ...dataInStore,
                allPortCostDefaults: {
                  ...allPortCostDefaults,
                  nodes: [...nodes, portCostDefault],
                },
              },
            });
          }
        } else if (event) {
          const id = +event;
          if (nodes.find((o) => o?.id === id)) {
            client.writeQuery({
              query: PortCostEstimatesDocument,
              data: {
                ...dataInStore,
                allPortCostDefaults: {
                  ...allPortCostDefaults,
                  nodes: nodes.filter((o) => o?.id !== id),
                },
              },
            });
          }
        }
      }
    },
  });

  const { refetch } = usePortCostsByPortAndPrimaryAttributesQuery({
    skip: true,
  });
  useShipUpdatedSubscription({
    variables: {
      org,
    },
    skip,
    onData: async function () {
      await refetch();
    },
  });

  const currencyRates = useCurrencyRates();

  return useMemo(
    () => portCostEstimatesToMaps(data, locationIdsByNames, currencyRates),
    [data, locationIdsByNames, currencyRates],
  );
}
